import { useState, useRef } from "react";
import "./ChatBot.scss";
import { useCollection } from "../../firebase-files/hooks/useCollection";
import { useNavigate } from "react-router-dom";
const features = [
  {
    title: "Users Details",
    collection: "users",
    about: "View and manage all users",
    icon: "👨",
    color: "#E67E22",
  },
  {
    title: "Orders Queries",
    collection: "orders",
    about: "Check and track all orders",
    icon: "👜",
    color: "#C1D82F",
  },
  {
    title: "Search Products",
    collection: "products",
    about: "Find products instantly",
    icon: "🔍",
    color: "#41C3FF",
  },
  {
    title: "In My Cart",
    collection: "cart",
    about: "Review items in your cart",
    icon: "🛒",
    color: "#C254FF",
  },
  {
    title: "My Cloud Coins",
    collection: "cloudCoins",
    about: "View and redeem your coins",
    icon: "🪙",
    color: "#7ED321",
  },
  {
    title: "General Queries",
    collection: "",
    about: "I always ready to help you!",
    icon: "❓",
    color: "#ffffffff",
  },
];

const ChatBot = () => {
  const navigate = useNavigate();

  const { documents: allUsers } = useCollection("users");
  const { documents: allOrders } = useCollection("orders");
  const { documents: allProducts } = useCollection("products");
  const { documents: cartData } = useCollection("cart");
  const { documents: allCoins } = useCollection("cloudCoins");

  const [messages, setMessages] = useState([
    { text: "Hello! How can I help you?", sender: "bot" },
  ]);

  const [input, setInput] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [listening, setListening] = useState(false);
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);

  const recognitionRef = useRef(null);
  const collectionDataMap = {
    users: allUsers,
    orders: allOrders,
    products: allProducts,
    cart: cartData,
    cloudCoins: allCoins,
  };
  const callGeminiAPI = async (promptText) => {
    const apiKey = "AIzaSyD_M0FyswLt4FYQpbiyZx8dsk6y8NT_pSo";
    const url =
      "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent";

    const body = {
      contents: [{ parts: [{ text: promptText }] }],
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-goog-api-key": apiKey,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("API call failed:", error);
      return null;
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    if (selectedCollection === null) return;

    setMessages((prev) => [...prev, { text: input.trim(), sender: "user" }]);
    setInput("");

    let prompt;

    if (selectedCollection === "") {
      // ✅ For General Queries - no Firestore data
      prompt = `You are a helpful AI assistant. Please answer the following general question:
${input}`;
    } else {
      // ✅ For Firestore-based collections
      const dataFromCollection = collectionDataMap[selectedCollection] || [];
      const limitedData = dataFromCollection.slice(0, 20);
      const jsonString = JSON.stringify(limitedData);

      prompt = `Here is the list of ${selectedCollection} data: ${jsonString}
Answer the following question about this data concisely:
${input}`;
    }

    const result = await callGeminiAPI(prompt);

    const botReply =
      result?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .join("") || "Sorry, no response from AI.";

    setMessages((prev) => [...prev, { text: botReply, sender: "bot" }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  // 🎤 Speech-to-Text
  const handleMicClick = () => {
    if (!("webkitSpeechRecognition" in window)) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    if (!recognitionRef.current) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.lang = "en-US";
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => setListening(true);
      recognition.onend = () => setListening(false);
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setListening(false);
      };
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
      };

      recognitionRef.current = recognition;
    }

    if (listening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
  };

  return (
    <div className="app-container">
      {showSettingsPopup && (
        <div className="settings-popup-overlay">
          <div className="settings-popup">
            <div className="settings-header">
              <h2>Settings</h2>
              <button
                className="close-btn"
                onClick={() => setShowSettingsPopup(false)}
              >
                ×
              </button>
            </div>
            <hr />
            {/* <br /> */}
            <div className="settings-section">
              <h4>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="21px"
                  viewBox="0 -960 960 960"
                  width="21px"
                  fill="#e3e3e3"
                >
                  <path d="M492-280q83 0 141.5-58.5T692-480q0-83-58.5-141.5T492-680q-22 0-43 4.5T408-662q54 25 85.5 74T525-480q0 59-31.5 108T408-298q20 9 41 13.5t43 4.5ZM480-28 346-160H160v-186L28-480l132-134v-186h186l134-132 134 132h186v186l132 134-132 134v186H614L480-28Zm0-112 100-100h140v-140l100-100-100-100v-140H580L480-820 380-720H240v140L140-480l100 100v140h140l100 100Zm0-340Z" />
                </svg>{" "}
                Theme
              </h4>
              <div className="btn-group">
                <button className="active">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="21px"
                    viewBox="0 -960 960 960"
                    width="21px"
                    fill="#2bd1c4"
                  >
                    <path d="M600-640 480-760l120-120 120 120-120 120Zm200 120-80-80 80-80 80 80-80 80ZM483-80q-84 0-157.5-32t-128-86.5Q143-253 111-326.5T79-484q0-146 93-257.5T409-880q-18 99 11 193.5T520-521q71 71 165.5 100T879-410q-26 144-138 237T483-80Zm0-80q88 0 163-44t118-121q-86-8-163-43.5T463-465q-61-61-97-138t-43-163q-77 43-120.5 118.5T159-484q0 135 94.5 229.5T483-160Zm-20-305Z" />
                  </svg>{" "}
                  Dark
                </button>
                <button>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="21px"
                    viewBox="0 -960 960 960"
                    width="21px"
                    fill="#e3e3e3"
                  >
                    <path d="M480-360q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35Zm0 80q-83 0-141.5-58.5T280-480q0-83 58.5-141.5T480-680q83 0 141.5 58.5T680-480q0 83-58.5 141.5T480-280ZM200-440H40v-80h160v80Zm720 0H760v-80h160v80ZM440-760v-160h80v160h-80Zm0 720v-160h80v160h-80ZM256-650l-101-97 57-59 96 100-52 56Zm492 496-97-101 53-55 101 97-57 59Zm-98-550 97-101 59 57-100 96-56-52ZM154-212l101-97 55 53-97 101-59-57Zm326-268Z" />
                  </svg>{" "}
                  Light
                </button>
              </div>
            </div>

            <div className="settings-section">
              <h4>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="21px"
                  viewBox="0 -960 960 960"
                  width="21px"
                  fill="#e3e3e3"
                >
                  <path d="M560-160v-520H360v-120h520v120H680v520H560Zm-360 0v-320H80v-120h360v120H320v320H200Z" />
                </svg>{" "}
                Font Size
              </h4>
              <div className="btn-group">
                <button>Small</button>
                <button className="active">Medium</button>
                <button>Large</button>
              </div>
            </div>

            <div className="settings-section">
              <h4>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="21px"
                  viewBox="0 -960 960 960"
                  width="21px"
                  fill="#e3e3e3"
                >
                  <path d="M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q83 0 155.5 31.5t127 86q54.5 54.5 86 127T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Zm0-82q26-36 45-75t31-83H404q12 44 31 83t45 75Zm-104-16q-18-33-31.5-68.5T322-320H204q29 50 72.5 87t99.5 55Zm208 0q56-18 99.5-55t72.5-87H638q-9 38-22.5 73.5T584-178ZM170-400h136q-3-20-4.5-39.5T300-480q0-21 1.5-40.5T306-560H170q-5 20-7.5 39.5T160-480q0 21 2.5 40.5T170-400Zm216 0h188q3-20 4.5-39.5T580-480q0-21-1.5-40.5T574-560H386q-3 20-4.5 39.5T380-480q0 21 1.5 40.5T386-400Zm268 0h136q5-20 7.5-39.5T800-480q0-21-2.5-40.5T790-560H654q3 20 4.5 39.5T660-480q0 21-1.5 40.5T654-400Zm-16-240h118q-29-50-72.5-87T584-782q18 33 31.5 68.5T638-640Zm-234 0h152q-12-44-31-83t-45-75q-26 36-45 75t-31 83Zm-200 0h118q9-38 22.5-73.5T376-782q-56 18-99.5 55T204-640Z" />
                </svg>{" "}
                Language
              </h4>
              <select>
                <option>English</option>
                <option>Hindi</option>
              </select>
            </div>

            <div className="settings-section toggles">
              <label className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="21px"
                    viewBox="0 -960 960 960"
                    width="21px"
                    fill="#e3e3e3"
                  >
                    <path d="M560-131v-82q90-26 145-100t55-168q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 127-78 224.5T560-131ZM120-360v-240h160l200-200v640L280-360H120Zm440 40v-322q47 22 73.5 66t26.5 96q0 51-26.5 94.5T560-320ZM400-606l-86 86H200v80h114l86 86v-252ZM300-480Z" />
                  </svg>
                  &nbsp; Sound Effects
                </div>
                <input type="checkbox" defaultChecked />
              </label>
              <label className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="21px"
                    viewBox="0 -960 960 960"
                    width="21px"
                    fill="#e3e3e3"
                  >
                    <path d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z" />
                  </svg>
                  &nbsp; Auto Save Chats
                </div>
                <input type="checkbox" defaultChecked />
              </label>
            </div>
            <hr />
            {/* <br /> */}
            <div className="settings-actions">
              <button className="reset-btn">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="21px"
                  viewBox="0 -960 960 960"
                  width="21px"
                  fill="#e3e3e3"
                >
                  <path d="M440-122q-121-15-200.5-105.5T160-440q0-66 26-126.5T260-672l57 57q-38 34-57.5 79T240-440q0 88 56 155.5T440-202v80Zm80 0v-80q87-16 143.5-83T720-440q0-100-70-170t-170-70h-3l44 44-56 56-140-140 140-140 56 56-44 44h3q134 0 227 93t93 227q0 121-79.5 211.5T520-122Z" />
                </svg>{" "}
                Reset
              </button>
              <button className="save-btn">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="21px"
                  viewBox="0 -960 960 960"
                  width="21px"
                  fill="#e3e3e3"
                >
                  <path d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z" />
                </svg>{" "}
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside className="sidebar">
        <div className="logo">
          <span className="logo-icon">
            <img src="https://assets-v2.lottiefiles.com/a/b942abb8-d62e-11ee-a179-af4105107ebe/tPZDd31PcO.gif" />
          </span>
          Botify
        </div>
        <br />
        <button
          className="new-chat-btn"
          onClick={() => {
            setSelectedCollection(null);
            setMessages([
              { text: "Hello! How can I help you?", sender: "bot" },
            ]);
            setShowChat(false);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24px"
            viewBox="0 -960 960 960"
            width="24px"
            fill="#e3e3e3"
          >
            <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
          </svg>
          New Chat
        </button>
        <nav className="sidebar-nav">
          <button className="active">
            {" "}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="#e3e3e3"
            >
              <path d="M240-400h320v-80H240v80Zm0-120h480v-80H240v80Zm0-120h480v-80H240v80ZM80-80v-720q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H240L80-80Zm126-240h594v-480H160v525l46-45Zm-46 0v-480 480Z" />
            </svg>
            &nbsp;Chats
          </button>
          <button onClick={() => setShowSettingsPopup(true)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="#e3e3e3"
            >
              <path d="M480-480ZM370-80l-16-128q-13-5-24.5-12T307-235l-119 50L78-375l103-78q-1-7-1-13.5v-27q0-6.5 1-13.5L78-585l110-190 119 50q11-8 23-15t24-12l16-128h220l16 128q13 5 24.5 12t22.5 15l119-50 110 190-74 56q-22-11-45-18.5T714-558l63-48-39-68-99 42q-22-23-48.5-38.5T533-694l-13-106h-79l-14 106q-31 8-57.5 23.5T321-633l-99-41-39 68 86 64q-5 15-7 30t-2 32q0 16 2 31t7 30l-86 65 39 68 99-42q17 17 36.5 30.5T400-275q1 57 23.5 107T484-80H370Zm41-279q6-20 14.5-38.5T445-433q-11-8-17-20.5t-6-26.5q0-25 17.5-42.5T482-540q14 0 27 6.5t21 17.5q17-11 35-19.5t38-13.5q-18-32-50-51.5T482-620q-59 0-99.5 41T342-480q0 38 18.5 70.5T411-359Zm269 199 120-120-120-120-28 28 72 72H560v40h163l-71 72 28 28Zm0 80q-83 0-141.5-58.5T480-280q0-83 58.5-141.5T680-480q83 0 141.5 58.5T880-280q0 83-58.5 141.5T680-80Z" />
            </svg>
            &nbsp;Settings
          </button>
          <button
            onClick={() => {
              navigate(`/`);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="#e3e3e3"
            >
              <path d="M480-120q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-480q0-75 28.5-140.5t77-114q48.5-48.5 114-77T480-840v80q-117 0-198.5 81.5T200-480q0 117 81.5 198.5T480-200v80Zm160-160-56-57 103-103H360v-80h327L584-624l56-56 200 200-200 200Z" />
            </svg>
            &nbsp;Exit
          </button>
        </nav>
        <div className="user-info">
          <img
            src="http://localhost:3000/assets/img/profile-option-6.jfif"
            alt="User"
            className="user-avatar"
          />
          <div className="user-details">
            <div className="user-name">Naman</div>
            <div className="user-email">naman@gmail.com</div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        <header className="chatbot-header">
          <div className="bot-header-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="#e3e3e3"
            >
              <path d="M160-120v-200q0-33 23.5-56.5T240-400h480q33 0 56.5 23.5T800-320v200H160Zm200-320q-83 0-141.5-58.5T160-640q0-83 58.5-141.5T360-840h240q83 0 141.5 58.5T800-640q0 83-58.5 141.5T600-440H360ZM240-200h480v-120H240v120Zm120-320h240q50 0 85-35t35-85q0-50-35-85t-85-35H360q-50 0-85 35t-35 85q0 50 35 85t85 35Zm0-80q17 0 28.5-11.5T400-640q0-17-11.5-28.5T360-680q-17 0-28.5 11.5T320-640q0 17 11.5 28.5T360-600Zm240 0q17 0 28.5-11.5T640-640q0-17-11.5-28.5T600-680q-17 0-28.5 11.5T560-640q0 17 11.5 28.5T600-600ZM480-200Zm0-440Z" />
            </svg>
          </div>
          <div>
            <h1>Hi, I’m Botify</h1>
            <p>I'm ready to help you.</p>
          </div>
        </header>

        {!showChat ? (
          <section className="features-section">
            <div className="features-intro">Here is what You'll get</div>
            <div className="features-grid">
              {features.map(
                ({ title, collection, about, icon, color }, idx) => (
                  <div
                    key={idx}
                    className="feature-card"
                    onClick={() => {
                      setSelectedCollection(collection);
                      setShowChat(true); // show chat
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className="feature-icon"
                      style={{ backgroundColor: color }}
                      aria-label={title}
                    >
                      {icon}
                    </div>
                    <div className="feature-title">{title}</div>
                    <div className="feature-about">{about}</div>
                    <div
                      className="feature-progress"
                      style={{ borderColor: color }}
                    />
                  </div>
                )
              )}
            </div>
          </section>
        ) : (
          <>
            {/* Chat board */}
            <div className="chatbot-messages">
              {messages &&
                messages.map((msg, idx) => {
                  const time = new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  });

                  return (
                    <div
                      key={idx}
                      className={`chat-row ${
                        msg.sender === "user" ? "user" : "bot"
                      }`}
                    >
                      {/* Bot/User Icon */}
                      {msg.sender === "bot" && (
                        <div className="chat-icon">🤖</div>
                      )}

                      <div className="chat-content">
                        <div className={`chat-message ${msg.sender}`}>
                          {msg.text}
                        </div>
                        <div className={`chat-time ${msg.sender}`}>{time}</div>
                      </div>

                      {msg.sender === "user" && (
                        <div className="chat-icon">👤</div>
                      )}
                    </div>
                  );
                })}
            </div>

            <footer className="chatbot-input-area">
              <input
                type="text"
                placeholder="Type a message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <button onClick={handleMicClick}>
                {listening ? "🎙️" : "🎤"}
              </button>
              <button onClick={handleSend}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="21px"
                  viewBox="0 -960 960 960"
                  width="21px"
                  fill="#e3e3e3"
                >
                  <path d="M120-160v-640l760 320-760 320Zm80-120 474-200-474-200v140l240 60-240 60v140Zm0 0v-400 400Z" />
                </svg>
              </button>
            </footer>
          </>
        )}
      </main>
    </div>
  );
};

export default ChatBot;
